/**
 * SubventionCampagnePanel — réglage de la campagne de subventions (item 9.6).
 *
 * Le maire fixe, pour un exercice :
 *   · une date limite de dépôt PAR NATURE (projet / fonctionnement) ;
 *   · le délai de remise du compte rendu financier.
 *
 * Les RELANCES elles-mêmes ne se règlent pas ici : leurs délais (1 à 2 rappels)
 * vivent dans l'écran « Échéances & rappels », commun à toute l'application
 * (socle mig 211). On évite ainsi deux endroits où régler la même chose.
 *
 * Pourquoi deux dates : les collectivités font les deux. Certaines fixent une
 * date unique pour tout, d'autres séparent selon la nature. Une commune qui veut
 * une date unique saisit simplement la même valeur des deux côtés.
 */
import { useEffect, useState } from 'react';
import { Card, Button, Alert } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { CalendarClock, Info } from 'lucide-react';
import type { SubventionCampagne } from '@roronoa-collectivites/shared-types';

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

export function SubventionCampagnePanel() {
  const anneeCourante = new Date().getFullYear();
  const [exercice, setExercice] = useState(anneeCourante);
  const [campagnes, setCampagnes] = useState<SubventionCampagne[]>([]);
  const [ouvProjet, setOuvProjet] = useState('');
  const [ouvFonct, setOuvFonct] = useState('');
  const [projet, setProjet] = useState('');
  const [fonctionnement, setFonctionnement] = useState('');
  const [delaiMois, setDelaiMois] = useState(6);
  const [moisCloture, setMoisCloture] = useState(12);
  const [actif, setActif] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function charger() {
    setLoading(true); setError(null);
    try {
      const l = await api.associations.subventions.campagnes();
      setCampagnes(l);
      const c = l.find((x) => x.exercice === exercice);
      setOuvProjet(c?.dateOuvertureProjet ?? '');
      setOuvFonct(c?.dateOuvertureFonctionnement ?? '');
      setProjet(c?.dateLimiteProjet ?? '');
      setFonctionnement(c?.dateLimiteFonctionnement ?? '');
      setDelaiMois(c?.justificatifDelaiMois ?? 6);
      setMoisCloture(c?.justificatifMoisCloture ?? 12);
      setActif(c?.actif ?? true);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }
  useEffect(() => { void charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [exercice]);

  async function enregistrer() {
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.associations.subventions.enregistrerCampagne({
        exercice,
        dateOuvertureProjet: ouvProjet || null,
        dateOuvertureFonctionnement: ouvFonct || null,
        dateLimiteProjet: projet || null,
        dateLimiteFonctionnement: fonctionnement || null,
        justificatifDelaiMois: delaiMois,
        justificatifMoisCloture: moisCloture,
        actif,
      });
      setMsg('Campagne enregistrée. Les relances ont été reprogrammées.');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  const champ = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink';
  const label = 'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <CalendarClock className="h-4 w-4 text-asso" aria-hidden="true" />
            Campagne de subventions
          </h3>
          <label className="ml-auto flex items-center gap-2 text-sm text-ink-muted">
            Exercice
            <select value={exercice} onChange={(e) => setExercice(Number(e.target.value))}
              className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              {[anneeCourante - 1, anneeCourante, anneeCourante + 1].map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
        </div>

        {loading ? (
          <p className="text-sm text-ink-subtle">Chargement…</p>
        ) : (
          <div className="space-y-4">
            {/*
              Ouverture puis échéance : deux moments distincts du calendrier.
              L'annonce d'ouverture (6 mois avant par défaut) laisse le temps à
              une association de préparer son dossier — c'est le rythme d'une AG.
            */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>Ouverture du dépôt — projet</label>
                <input type="date" value={ouvProjet} onChange={(e) => setOuvProjet(e.target.value)} className={champ} />
              </div>
              <div>
                <label className={label}>Ouverture du dépôt — fonctionnement</label>
                <input type="date" value={ouvFonct} onChange={(e) => setOuvFonct(e.target.value)} className={champ} />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>Date limite — subventions projet</label>
                <input type="date" value={projet} onChange={(e) => setProjet(e.target.value)} className={champ} />
              </div>
              <div>
                <label className={label}>Date limite — subventions fonctionnement</label>
                <input type="date" value={fonctionnement} onChange={(e) => setFonctionnement(e.target.value)} className={champ} />
              </div>
            </div>

            <p className="flex items-start gap-1.5 text-xs text-ink-muted">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                L'<strong>ouverture</strong> est annoncée à toutes les associations (6 mois
                puis 1 mois avant, par défaut). L'<strong>échéance</strong> ne relance que
                celles <strong>n'ayant pas déposé</strong>. Une date vide = pas de rappel
                correspondant. Pour une date unique, saisissez la même des deux côtés.
              </span>
            </p>

            <div className="border-t border-border pt-4">
              <p className="mb-2 text-sm font-medium text-ink">Comptes rendus financiers</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={label}>Clôture de l'exercice associatif</label>
                  <select value={moisCloture} onChange={(e) => setMoisCloture(Number(e.target.value))} className={champ}>
                    {MOIS.map((m, i) => <option key={m} value={i + 1}>fin {m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Délai de remise (mois après clôture)</label>
                  <input type="number" min={1} max={36} value={delaiMois}
                    onChange={(e) => setDelaiMois(Number(e.target.value))} className={champ} />
                </div>
              </div>
              <p className="mt-2 flex items-start gap-1.5 text-xs text-ink-muted">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  6 mois est le délai légal du compte rendu financier (article 10 de la
                  loi n° 2000-321). Les associations dont la subvention est versée sans
                  compte rendu reçu sont relancées avant l'échéance.
                </span>
              </p>
            </div>

            <label className="flex items-center gap-2 border-t border-border pt-4 text-sm text-ink">
              <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)}
                className="h-4 w-4 rounded border-border" />
              Campagne active (décocher suspend toutes les relances de cet exercice)
            </label>

            <div className="flex justify-end">
              <Button variant="primary" loading={saving} onClick={() => void enregistrer()}>
                Enregistrer
              </Button>
            </div>
          </div>
        )}
      </Card>

      {campagnes.length > 0 && (
        <Card variant="glass" padded>
          <p className="mb-2 text-sm font-semibold text-ink">Exercices réglés</p>
          <ul className="space-y-1 text-sm text-ink-muted">
            {campagnes.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink tabular-nums">{c.exercice}</span>
                <span>
                  projet : {c.dateLimiteProjet ? c.dateLimiteProjet.split('-').reverse().join('/') : '—'}
                  {' · '}
                  fonctionnement : {c.dateLimiteFonctionnement ? c.dateLimiteFonctionnement.split('-').reverse().join('/') : '—'}
                </span>
                {!c.actif && (
                  <span className="rounded-r-pill bg-surface px-1.5 py-0.5 text-[9px] uppercase tracking-r-eyebrow text-ink-subtle">
                    suspendue
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
